import { useState } from 'react';
import type { WorkflowVersionSummary } from '@erp/rpc';
import { MAX_CHANGELOG_CHARS } from '@erp/workflows';
import { T, useGT } from 'gt-react';
import {
  CheckIcon,
  XIcon,
  CircleNotchIcon,
  SparkleIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import {
  useRegenerateChangelog,
  useUpdateVersionChangelog,
} from '@/api/rpc/hooks/workflows/useWorkflowVersioning';
import { useToast } from '@/context/ToastContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ChangelogText } from './ChangelogText';

export function VersionChangelogEditor({
  version,
}: {
  version: WorkflowVersionSummary;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const changelogMutation = useUpdateVersionChangelog();
  const regenerateMutation = useRegenerateChangelog();
  const [editingChangelog, setEditingChangelog] = useState(false);
  const [changelogDraft, setChangelogDraft] = useState(version.changelog ?? '');
  const canRegenerate = version.status !== 'draft' && !!version.parentVersionId;
  const busy = changelogMutation.isPending || regenerateMutation.isPending;
  const handleRegenerate = async () => {
    try {
      await regenerateMutation.mutateAsync({
        workflowDefinitionId: version.id,
      });
      showToast({
        title: gt('Changelog regenerated'),
        message: '',
        color: 'success',
      });
    } catch (err) {
      showToast({
        title: gt('Could not regenerate changelog'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    }
  };

  const handleSaveChangelog = async () => {
    try {
      await changelogMutation.mutateAsync({
        workflowDefinitionId: version.id,
        changelog: changelogDraft,
      });
      setEditingChangelog(false);
    } catch (err) {
      showToast({
        title: gt('Failed to update changelog'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    }
  };

  return (
    <div className="mt-2">
      {editingChangelog ? (
        <div className="space-y-2">
          <Textarea
            value={changelogDraft}
            onChange={(e) => setChangelogDraft(e.target.value)}
            rows={5}
            maxLength={MAX_CHANGELOG_CHARS}
            aria-label={gt('Changelog', {
              context: 'workflow version changelog editor',
            })}
            placeholder={gt('Describe what changed in this version…')}
          />
          <p className="text-xs text-muted-foreground">
            <T context="workflow changelog editing hint">
              Start with a short subject. Add a blank line, then bullet points
              for details.
            </T>
          </p>
          <div className="flex gap-1">
            <Button size="sm" onClick={handleSaveChangelog} disabled={busy}>
              <CheckIcon className="mr-1 h-3 w-3" /> <T>Save</T>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditingChangelog(false);
                setChangelogDraft(version.changelog ?? '');
              }}
            >
              <XIcon className="mr-1 h-3 w-3" /> <T>Cancel</T>
            </Button>
          </div>
        </div>
      ) : (
        <div className="group flex items-start gap-2">
          <div className="min-w-0 flex-1 text-sm">
            {regenerateMutation.isPending ? (
              // The AI summary is awaited server-side, so this spinner covers the
              // real wait and the text that replaces it is the finished changelog.
              <span className="flex items-center gap-1.5 italic text-muted-foreground">
                <CircleNotchIcon className="h-3 w-3 animate-spin" />
                <T>Writing changelog…</T>
              </span>
            ) : (
              <ChangelogText changelog={version.changelog} />
            )}
          </div>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
            {canRegenerate && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRegenerate}
                disabled={busy}
                title={gt('Regenerate AI changelog from the diff')}
              >
                {regenerateMutation.isPending ? (
                  <CircleNotchIcon className="h-3 w-3 animate-spin" />
                ) : (
                  <SparkleIcon className="h-3 w-3" />
                )}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setChangelogDraft(version.changelog ?? '');
                setEditingChangelog(true);
              }}
              aria-label={gt('Edit Changelog', {
                context: 'workflow version changelog action',
              })}
            >
              <PencilSimpleIcon className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
